import React, { useId, useMemo, useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Button from '../UI/Button'
import {
  BARE_OWNERSHIP,
  FULL_OWNERSHIP,
  OWNERSHIP_RIGHT_OPTIONS,
  USUFRUCT,
  calculateOwnershipSummary,
  convertOwnershipPlanToSimple,
  createHolder,
  createPortion,
  createRight,
  evenShares,
  formatOwnershipPercentage,
  getRightLabel,
  rebalanceHolders,
  validateOwnershipPlan,
} from './ownershipPlan'
import './OwnershipComposer.css'


const EXPIRING_RIGHT_TYPES = new Set([
  'EMPHYTEUSIS_GRANTOR',
  'EMPHYTEUSIS_HOLDER',
  'SUPERFICIES_GRANTOR',
  'SUPERFICIES_HOLDER',
])

const OWNERSHIP_RIGHT_ABBREVIATIONS = {
  [FULL_OWNERSHIP]: 'VE',
  [USUFRUCT]: 'VG',
  [BARE_OWNERSHIP]: 'NE',
  EMPHYTEUSIS_GRANTOR: 'EPG',
  EMPHYTEUSIS_HOLDER: 'EPH',
  SUPERFICIES_GRANTOR: 'OG',
  SUPERFICIES_HOLDER: 'OH',
}

const partyKey = party => `${party.type}:${party.id}`

const partyName = party => {
  if (!party) return 'Unknown party'
  if (party.type === 'company') return party.company_name || party.name || 'Unnamed company'
  return `${party.first_name || ''} ${party.last_name || ''}`.trim() || party.name || 'Unnamed contact'
}

const cloneHolders = holders => holders.map(holder => createHolder(holder.party, holder.sharePercentage))

const asInputPercentage = value => String(value ?? '').replace(',', '.')

function OwnershipComposer({
  title,
  plan,
  onChange,
  contacts = [],
  companies = [],
  calculatedShareLabel = 'Calculated economic share',
}) {
  const contentId = useId()
  const [expanded, setExpanded] = useState(false)
  const [expandedPortionIds, setExpandedPortionIds] = useState(() => new Set())
  const [queries, setQueries] = useState({})
  const allParties = useMemo(() => [
    ...contacts.map(contact => ({ ...contact, type: 'contact' })),
    ...companies.map(company => ({ ...company, type: 'company' })),
  ], [contacts, companies])

  const validation = validateOwnershipPlan(plan)
  const summary = calculateOwnershipSummary(plan)
  const firstPortion = plan.portions?.[0]
  const firstRight = firstPortion?.rights?.[0]
  const isSimpleOwnership = plan.portions?.length === 1
    && firstPortion?.structureType === 'FULL'
    && firstPortion?.rights?.length === 1
    && firstRight?.type === FULL_OWNERSHIP
  const partyNoun = title.toLowerCase().startsWith('seller') ? 'seller' : 'buyer'
  const partyCountLabel = summary.length === 1 ? partyNoun : `${partyNoun}s`
  const hasParties = summary.length > 0
  const statusLabel = validation.valid ? 'Complete' : hasParties ? 'Needs review' : 'Not configured'
  const headerSummary = hasParties
    ? `${summary.length} ${partyCountLabel} · ${formatOwnershipPercentage(validation.invoiceTotal)}% allocated`
    : `No ${partyNoun}s added`

  const setPlanMode = mode => {
    if (mode === 'SIMPLE') {
      if (!isSimpleOwnership) onChange(convertOwnershipPlanToSimple(plan))
      return
    }
    if (isSimpleOwnership) {
      setExpandedPortionIds(new Set())
      setStructureType(firstPortion, 'SPLIT')
    }
  }

  const togglePortion = portionId => {
    setExpandedPortionIds(current => {
      const next = new Set(current)
      if (next.has(portionId)) next.delete(portionId)
      else next.add(portionId)
      return next
    })
  }

  const updatePortion = (portionId, updater) => {
    onChange({
      ...plan,
      portions: plan.portions.map(portion => (
        portion.id === portionId ? updater(portion) : portion
      )),
    })
  }

  const updateRight = (portionId, rightId, updater) => {
    updatePortion(portionId, portion => ({
      ...portion,
      rights: portion.rights.map(right => right.id === rightId ? updater(right) : right),
    }))
  }

  const setStructureType = (portion, structureType) => {
    if (structureType === portion.structureType) return

    if (structureType === 'SPLIT') {
      const existingHolders = portion.rights[0]?.holders || []
      updatePortion(portion.id, current => ({
        ...current,
        structureType: 'SPLIT',
        rights: [
          createRight(USUFRUCT, '25', cloneHolders(existingHolders)),
          createRight(BARE_OWNERSHIP, '75', cloneHolders(existingHolders)),
        ],
      }))
      return
    }

    const allocations = new Map()
    portion.rights.forEach(right => {
      right.holders.forEach(holder => {
        const key = `${holder.partyType}:${holder.partyId}`
        const current = allocations.get(key) || { holder, share: 0 }
        current.share += Number(right.valuePercentage || 0) * Number(holder.sharePercentage || 0) / 100
        allocations.set(key, current)
      })
    })
    const holders = [...allocations.values()].map(item => createHolder(item.holder.party, String(item.share)))
    updatePortion(portion.id, current => ({
      ...current,
      structureType: 'FULL',
      rights: [createRight(FULL_OWNERSHIP, '100', holders)],
    }))
  }

  const addPortion = () => {
    const portions = [...plan.portions]
    if (!portions.length) {
      onChange({ ...plan, portions: [createPortion()] })
      return
    }

    let largestIndex = 0
    portions.forEach((portion, index) => {
      if (Number(portion.sharePercentage || 0) > Number(portions[largestIndex].sharePercentage || 0)) {
        largestIndex = index
      }
    })
    const largestShare = Number(portions[largestIndex].sharePercentage || 0)
    const newShare = Number((largestShare / 2).toFixed(6))
    portions[largestIndex] = {
      ...portions[largestIndex],
      sharePercentage: String(Number((largestShare - newShare).toFixed(6))),
    }
    portions.push(createPortion(String(newShare)))
    onChange({ ...plan, portions })
  }

  const removePortion = portionId => {
    const removed = plan.portions.find(portion => portion.id === portionId)
    const remaining = plan.portions.filter(portion => portion.id !== portionId)
    if (!remaining.length) return
    remaining[0] = {
      ...remaining[0],
      sharePercentage: String(Number(remaining[0].sharePercentage || 0) + Number(removed?.sharePercentage || 0)),
    }
    onChange({ ...plan, portions: remaining })
  }

  const addRight = portion => {
    const usedTypes = new Set(portion.rights.map(right => right.type))
    const nextType = OWNERSHIP_RIGHT_OPTIONS.find(option => (
      option.value !== FULL_OWNERSHIP && !usedTypes.has(option.value)
    ))?.value
    if (!nextType) return

    let largestIndex = 0
    portion.rights.forEach((right, index) => {
      if (Number(right.valuePercentage || 0) > Number(portion.rights[largestIndex].valuePercentage || 0)) {
        largestIndex = index
      }
    })
    const rights = [...portion.rights]
    const largestValue = Number(rights[largestIndex]?.valuePercentage || 0)
    const newValue = Number((largestValue / 2).toFixed(6))
    rights[largestIndex] = {
      ...rights[largestIndex],
      valuePercentage: String(Number((largestValue - newValue).toFixed(6))),
    }
    rights.push(createRight(nextType, String(newValue)))
    updatePortion(portion.id, current => ({ ...current, rights }))
  }

  const removeRight = (portion, rightId) => {
    const removed = portion.rights.find(right => right.id === rightId)
    const remaining = portion.rights.filter(right => right.id !== rightId)
    if (!remaining.length) return
    remaining[0] = {
      ...remaining[0],
      valuePercentage: String(Number(remaining[0].valuePercentage || 0) + Number(removed?.valuePercentage || 0)),
    }
    updatePortion(portion.id, current => ({ ...current, rights: remaining }))
  }

  const addHolder = (portionId, right, party) => {
    const holders = rebalanceHolders([...right.holders, createHolder(party)])
    updateRight(portionId, right.id, current => ({ ...current, holders }))
    setQueries(current => ({ ...current, [right.id]: '' }))
  }

  const removeHolder = (portionId, right, holderId) => {
    const holders = rebalanceHolders(right.holders.filter(holder => holder.id !== holderId))
    updateRight(portionId, right.id, current => ({ ...current, holders }))
  }

  const setEvenHolderShares = (portionId, right) => {
    const shares = evenShares(right.holders.length)
    updateRight(portionId, right.id, current => ({
      ...current,
      holders: current.holders.map((holder, index) => ({ ...holder, sharePercentage: shares[index] })),
    }))
  }

  const candidatesFor = right => {
    const query = String(queries[right.id] || '').trim().toLowerCase()
    if (!query) return []
    const assigned = new Set(right.holders.map(holder => `${holder.partyType}:${holder.partyId}`))
    return allParties.filter(party => {
      if (assigned.has(partyKey(party))) return false
      const searchable = `${partyName(party)} ${party.email || ''}`.toLowerCase()
      return searchable.includes(query)
    }).slice(0, 6)
  }

  const renderPartySearch = (portionId, right, placeholder) => {
    const candidates = candidatesFor(right)
    return (
      <div className='ownership-party-search'>
        <input
          type='search'
          value={queries[right.id] || ''}
          onChange={event => setQueries(current => ({ ...current, [right.id]: event.target.value }))}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        {queries[right.id]?.trim() && (
          <div className='ownership-party-search__results'>
            {candidates.length > 0 ? candidates.map(party => (
              <button type='button' key={partyKey(party)} onClick={() => addHolder(portionId, right, party)}>
                <strong>{partyName(party)}</strong>
                <span>{party.email || (party.type === 'company' ? 'Company' : 'Contact')}</span>
              </button>
            )) : (
              <span className='ownership-party-search__none'>No matching party available.</span>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderHolders = (portion, right, simple = false) => (
    <>
      {right.holders.length > 0 ? (
        <div className={`ownership-holder-list ${simple ? 'ownership-holder-list--simple' : ''}`}>
          <div className='ownership-holder-list__labels' aria-hidden='true'>
            <span>{simple ? 'Party' : 'Holder'}</span>
            <span>{simple ? 'Ownership' : 'Share of this right'}</span>
            <span />
          </div>
          {right.holders.map(holder => (
            <div className='ownership-holder' key={holder.id}>
              <div className='ownership-holder__party'>
                <strong>{partyName(holder.party)}</strong>
                <span>{holder.partyType === 'company' ? 'Company' : 'Contact'}</span>
              </div>
              <span className='ownership-percentage-input'>
                <input
                  type='text'
                  inputMode='decimal'
                  value={holder.sharePercentage}
                  onChange={event => updateRight(portion.id, right.id, current => ({
                    ...current,
                    holders: current.holders.map(item => item.id === holder.id ? {
                      ...item,
                      sharePercentage: asInputPercentage(event.target.value),
                    } : item),
                  }))}
                  aria-label={`${partyName(holder.party)} ${simple ? 'ownership' : `share of ${getRightLabel(right.type)}`}`}
                />
                <span>%</span>
              </span>
              <Button
                variant='ghost'
                size='sm'
                tone='red'
                onClick={() => removeHolder(portion.id, right, holder.id)}
                aria-label={`Remove ${partyName(holder.party)}`}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      ) : simple ? null : (
        <p className='ownership-empty'>No {simple ? partyNoun : 'holders'} added.</p>
      )}
    </>
  )

  const simpleHolderTotal = firstRight?.holders?.reduce(
    (sum, holder) => sum + Number(holder.sharePercentage || 0),
    0,
  ) || 0

  const renderPlanMode = () => (
    <div className='ownership-mode-row'>
      <div className='ownership-mode-toggle' role='group' aria-label={`${title} ownership mode`}>
        <button
          type='button'
          className={isSimpleOwnership ? 'is-active' : ''}
          aria-pressed={isSimpleOwnership}
          onClick={() => setPlanMode('SIMPLE')}
        >
          Simple ownership
        </button>
        <button
          type='button'
          className={!isSimpleOwnership ? 'is-active' : ''}
          aria-pressed={!isSimpleOwnership}
          onClick={() => setPlanMode('SPLIT')}
        >
          Split ownership
        </button>
      </div>
      <p>
        {isSimpleOwnership
          ? `One percentage per ${partyNoun}.`
          : 'Divide ownership into parts, rights, and holders.'}
      </p>
    </div>
  )

  return (
    <section className={`ownership-composer ${expanded ? 'is-open' : ''}`} aria-label={`${title} ownership`}>
      <button
        type='button'
        className='ownership-composer__toggle'
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded(current => !current)}
      >
        <span className='ownership-composer__identity'>
          <strong>{title}</strong>
          <span>{headerSummary}</span>
        </span>
        <span className='ownership-composer__toggle-end'>
          <span className={`ownership-composer__state ${validation.valid ? 'is-valid' : hasParties ? 'is-invalid' : ''}`}>
            {statusLabel}
          </span>
          <KeyboardArrowDownIcon className='ownership-composer__chevron' aria-hidden='true' />
        </span>
      </button>

      <div id={contentId} className='ownership-composer__body' hidden={!expanded}>
        {renderPlanMode()}
        {isSimpleOwnership ? (
          <>
            {renderHolders(firstPortion, firstRight, true)}
            {renderPartySearch(firstPortion.id, firstRight, `Add ${partyNoun}`)}

            <div className={`ownership-total ${hasParties && Math.abs(simpleHolderTotal - 100) > 0.000001 ? 'is-invalid' : ''}`}>
              <span>Total ownership</span>
              <strong>{formatOwnershipPercentage(simpleHolderTotal)} / 100%</strong>
            </div>
          </>
        ) : (
          <>
            <div className='ownership-guide' aria-label='Ownership calculation order'>
              <div><span>1</span><strong>Ownership parts</strong><small>Together 100%</small></div>
              <div><span>2</span><strong>Rights per part</strong><small>Values together 100%</small></div>
              <div><span>3</span><strong>Holders per right</strong><small>Shares together 100%</small></div>
            </div>

            <div className='ownership-composer__portions'>
              {plan.portions.map((portion, portionIndex) => {
                const usedRightTypes = new Set(portion.rights.map(right => right.type))
                const rightValueTotal = portion.rights.reduce((sum, right) => sum + Number(right.valuePercentage || 0), 0)
                const isPortionCollapsed = !expandedPortionIds.has(portion.id)
                const portionContentId = `${contentId}-${portion.id}`
                const portionStructureLabel = portion.structureType === 'SPLIT'
                  ? `${portion.rights.length} ownership rights`
                  : 'Full ownership'
                return (
                  <section className={`ownership-portion ${isPortionCollapsed ? 'is-collapsed' : ''}`} key={portion.id}>
                    <div className='ownership-portion__header'>
                      <button
                        type='button'
                        className='ownership-portion__toggle'
                        aria-expanded={!isPortionCollapsed}
                        aria-controls={portionContentId}
                        onClick={() => togglePortion(portion.id)}
                      >
                        <span>
                          <strong>{plan.portions.length === 1 ? 'Entire ownership' : `Ownership part ${portionIndex + 1}`}</strong>
                          <small>{formatOwnershipPercentage(portion.sharePercentage)}% of ownership · {portionStructureLabel}</small>
                        </span>
                        <KeyboardArrowDownIcon aria-hidden='true' />
                      </button>
                      {plan.portions.length > 1 && (
                        <Button variant='ghost' size='sm' tone='red' onClick={() => removePortion(portion.id)}>
                          Remove part
                        </Button>
                      )}
                    </div>

                    <div id={portionContentId} className='ownership-portion__body' hidden={isPortionCollapsed}>
                      <div className='ownership-fields ownership-fields--portion'>
                        <label className='ownership-field'>
                          <span>Share of ownership</span>
                          <span className='ownership-percentage-input'>
                            <input
                              type='text'
                              inputMode='decimal'
                              value={portion.sharePercentage}
                              onChange={event => updatePortion(portion.id, current => ({
                                ...current,
                                sharePercentage: asInputPercentage(event.target.value),
                              }))}
                              aria-label={`Ownership part ${portionIndex + 1} percentage`}
                            />
                            <span>%</span>
                          </span>
                        </label>

                        <div className='ownership-field'>
                          <span>Ownership of this part</span>
                          <div className='ownership-structure-toggle' role='group' aria-label={`Ownership part ${portionIndex + 1} structure`}>
                            <button
                              type='button'
                              className={portion.structureType === 'FULL' ? 'is-active' : ''}
                              aria-pressed={portion.structureType === 'FULL'}
                              onClick={() => setStructureType(portion, 'FULL')}
                            >
                              Full ownership
                            </button>
                            <button
                              type='button'
                              className={portion.structureType === 'SPLIT' ? 'is-active' : ''}
                              aria-pressed={portion.structureType === 'SPLIT'}
                              onClick={() => setStructureType(portion, 'SPLIT')}
                            >
                              Split rights
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className='ownership-rights'>
                        {portion.rights.map(right => {
                          const holderTotal = right.holders.reduce((sum, holder) => sum + Number(holder.sharePercentage || 0), 0)
                          return (
                            <section className='ownership-right' key={right.id}>
                              <div className='ownership-right__header'>
                                <div>
                                  <h3>{getRightLabel(right.type)}</h3>
                                  <span>{formatOwnershipPercentage(holderTotal)} / 100% assigned</span>
                                </div>
                                {portion.structureType === 'SPLIT' && portion.rights.length > 1 && (
                                  <Button variant='ghost' size='sm' tone='red' onClick={() => removeRight(portion, right.id)}>
                                    Remove right
                                  </Button>
                                )}
                              </div>

                              {portion.structureType === 'SPLIT' && (
                                <div className='ownership-fields ownership-fields--right'>
                                  <label className='ownership-field'>
                                    <span>Ownership right</span>
                                    <select
                                      value={right.type}
                                      onChange={event => updateRight(portion.id, right.id, current => ({
                                        ...current,
                                        type: event.target.value,
                                      }))}
                                    >
                                      {OWNERSHIP_RIGHT_OPTIONS.filter(option => option.value !== FULL_OWNERSHIP).map(option => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                          disabled={usedRightTypes.has(option.value) && option.value !== right.type}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>

                                  <label className='ownership-field'>
                                    <span>Share of purchase price</span>
                                    <span className='ownership-percentage-input'>
                                      <input
                                        type='text'
                                        inputMode='decimal'
                                        value={right.valuePercentage}
                                        onChange={event => updateRight(portion.id, right.id, current => ({
                                          ...current,
                                          valuePercentage: asInputPercentage(event.target.value),
                                        }))}
                                        aria-label={`${getRightLabel(right.type)} share of purchase price`}
                                      />
                                      <span>%</span>
                                    </span>
                                  </label>

                                  {EXPIRING_RIGHT_TYPES.has(right.type) && (
                                    <label className='ownership-field'>
                                      <span>Right end date</span>
                                      <input
                                        type='date'
                                        value={right.endDate || ''}
                                        onChange={event => updateRight(portion.id, right.id, current => ({
                                          ...current,
                                          endDate: event.target.value || null,
                                        }))}
                                      />
                                    </label>
                                  )}

                                  <label className='ownership-field ownership-field--wide'>
                                    <span>Valuation basis (optional)</span>
                                    <input
                                      type='text'
                                      value={right.valuationNote || ''}
                                      placeholder='Example: notarial calculation 08-09-2026'
                                      onChange={event => updateRight(portion.id, right.id, current => ({
                                        ...current,
                                        valuationNote: event.target.value,
                                      }))}
                                    />
                                  </label>
                                </div>
                              )}

                              {renderHolders(portion, right)}
                              <div className='ownership-holder-actions'>
                                {renderPartySearch(portion.id, right, `Add holder to ${getRightLabel(right.type).toLowerCase()}`)}
                                {right.holders.length > 1 && (
                                  <Button variant='ghost' size='sm' onClick={() => setEvenHolderShares(portion.id, right)}>
                                    Split evenly
                                  </Button>
                                )}
                              </div>
                            </section>
                          )
                        })}
                      </div>

                      {portion.structureType === 'SPLIT' && (
                        <div className='ownership-portion__footer'>
                          <span className={Math.abs(rightValueTotal - 100) <= 0.000001 ? '' : 'is-invalid'}>
                            Rights value: <strong>{formatOwnershipPercentage(rightValueTotal)} / 100%</strong>
                          </span>
                          <Button
                            variant='secondary'
                            size='sm'
                            onClick={() => addRight(portion)}
                            disabled={usedRightTypes.size >= OWNERSHIP_RIGHT_OPTIONS.length - 1}
                          >
                            Add ownership right
                          </Button>
                        </div>
                      )}
                    </div>
                  </section>
                )
              })}
            </div>

            <Button variant='secondary' size='sm' onClick={addPortion}>Add ownership part</Button>

            <div className='ownership-summary'>
              <div className='ownership-summary__header'>
                <div>
                  <h3>Calculated result</h3>
                  <p>Ownership part × right value × holder share.</p>
                </div>
                <strong>{formatOwnershipPercentage(validation.invoiceTotal)} / 100%</strong>
              </div>
              {summary.length > 0 ? (
                <div className='ownership-map' aria-label='Calculated ownership map'>
                  <div className='ownership-map__tree'>
                    <div className='ownership-map__node ownership-map__root'>
                      <span>Ownership</span>
                      <strong>100%</strong>
                    </div>
                    <div className='ownership-map__portions'>
                      {plan.portions.map((portion, portionIndex) => (
                        <div className='ownership-map__portion' key={portion.id}>
                          <div className='ownership-map__node'>
                            <span>{plan.portions.length === 1 ? 'Entire ownership' : `Ownership part ${portionIndex + 1}`}</span>
                            <strong>{formatOwnershipPercentage(portion.sharePercentage)}%</strong>
                          </div>
                          <div className='ownership-map__rights'>
                            {portion.rights.map(right => (
                              <div className='ownership-map__right' key={right.id}>
                                <div className='ownership-map__node'>
                                  <span title={getRightLabel(right.type)} aria-label={getRightLabel(right.type)}>
                                    {OWNERSHIP_RIGHT_ABBREVIATIONS[right.type] || getRightLabel(right.type)}
                                  </span>
                                  <strong>{formatOwnershipPercentage(right.valuePercentage)}% of part</strong>
                                </div>
                                <div className='ownership-map__holders'>
                                  {right.holders.length > 0 ? right.holders.map(holder => {
                                    const finalShare = Number(portion.sharePercentage || 0)
                                      * Number(right.valuePercentage || 0)
                                      * Number(holder.sharePercentage || 0)
                                      / 10000
                                    return (
                                      <div className='ownership-map__node ownership-map__holder' key={holder.id}>
                                        <span>{partyName(holder.party)}</span>
                                        <strong>{formatOwnershipPercentage(finalShare)}% final</strong>
                                      </div>
                                    )
                                  }) : (
                                    <div className='ownership-map__node ownership-map__holder is-empty'>
                                      <span>No holder</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='ownership-map__totals'>
                    <span>Final {calculatedShareLabel.toLowerCase()}</span>
                    <div>
                      {summary.map(item => (
                        <span key={item.key}>
                          {partyName(item.party)} <strong>{formatOwnershipPercentage(item.invoicePercentage)}%</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className='ownership-empty'>Add holders to see calculated shares.</p>
              )}
            </div>
          </>
        )}

        {hasParties && !validation.valid && (
          <div className='ownership-errors' role='alert'>
            <strong>Ownership is not complete yet.</strong>
            <ul>
              {validation.errors.slice(0, 4).map(error => <li key={error}>{error}</li>)}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}

export default OwnershipComposer
